import React, {
  useMemo,
  useEffect,
  useState,
} from 'react';

import {
  Container,
  Stack,
} from '@mantine/core';

import { Helmet } from 'react-helmet-async';

import { useAppSelector } from '../../store/hooks';

import { HeroCarousel } from '../../components/common/HeroCarousel';

import { OurProducts } from '../../components/home/OurProducts';

import { OurQuality } from '../../components/home/OurQuality';

import { InternationalExport } from '../../components/home/InternationalExport';

import { Certifications } from '../../components/home/Certifications';

import { BlogSection } from '../../components/home/BlogSection';

import { useSEO } from '../../hooks/useSEO';

export const HomePage: React.FC = () => {
  const { content } = useAppSelector(
    (state) => state.content
  );

  // 🔥 Safe fallback
  const home = content?.pages?.home;

  const { seo } = useSEO('home');

  // 🔥 Cached home content
  const [cachedHome, setCachedHome] =
    useState(home);

  // 🔥 Cache home page data
  useEffect(() => {
    const cached = sessionStorage.getItem(
      'home_page_data'
    );

    // Load cache instantly
    if (cached && cached !== 'undefined') {
      try {
        const parsed = JSON.parse(cached);

        setCachedHome(parsed);
      } catch (e) {
        console.error(
          'Home cache parse error:',
          e
        );
      }
    }

    // Save latest content
    if (home) {
      setCachedHome(home);

      sessionStorage.setItem(
        'home_page_data',
        JSON.stringify(home)
      );
    }
  }, [home]);

  // 🔥 Memoized sections
  const sections = useMemo(
    () => cachedHome?.sections || {},
    [cachedHome]
  );

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>
          {seo?.metaTitle ||
            'Thalvora Exports'}
        </title>

        <meta
          name="description"
          content={
            seo?.metaDescription || ''
          }
        />

        {seo?.metaKeywords && (
          <meta
            name="keywords"
            content={seo.metaKeywords.join(
              ', '
            )}
          />
        )}

        {seo?.canonicalUrl && (
          <link
            rel="canonical"
            href={seo.canonicalUrl}
          />
        )}

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content={
            seo?.ogTitle ||
            seo?.metaTitle
          }
        />

        <meta
          property="og:description"
          content={
            seo?.ogDescription ||
            seo?.metaDescription
          }
        />

        {seo?.ogImage && (
          <meta
            property="og:image"
            content={seo.ogImage}
          />
        )}

        {seo?.canonicalUrl && (
          <meta
            property="og:url"
            content={seo.canonicalUrl}
          />
        )}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={
            seo?.twitterTitle ||
            seo?.metaTitle
          }
        />

        <meta
          name="twitter:description"
          content={
            seo?.twitterDescription ||
            seo?.metaDescription
          }
        />

        {seo?.twitterImage && (
          <meta
            name="twitter:image"
            content={seo.twitterImage}
          />
        )}

        <meta
          name="robots"
          content="index, follow"
        />

        {seo?.structuredData && (
          <script type="application/ld+json">
            {seo.structuredData}
          </script>
        )}
      </Helmet>

      {/* HERO */}
      <HeroCarousel />

      {/* SECTIONS */}
      <Container size="xl" py={50}>
        <Stack gap={60}>
          {sections?.products
            ?.isActive && <OurProducts />}

          {sections?.quality
            ?.isActive && <OurQuality />}

          {sections?.international
            ?.isActive && (
            <InternationalExport />
          )}

          {sections?.certifications
            ?.isActive && (
            <Certifications />
          )}

          {sections?.blog?.isActive && (
            <BlogSection />
          )}
        </Stack>
      </Container>
    </>
  );
};